import { supabase } from './supabase';
import type { Message, StructuredPortrait } from '../types';
import { clearFlowDraft, readDraftAnswers } from './almaFlow/draft';
import type { AlmaFlowAnswers } from './almaFlow/types';

/**
 * The /try Alma chat is drafted anonymously and only persisted once the
 * traveler authenticates. Google OAuth does a full-page redirect (unlike the
 * in-modal email/password path), so the draft has to survive in localStorage
 * and be picked up wherever the session actually lands — see AuthCallback.
 */
export const TRY_CHAT_STORAGE_KEY = 'sekretly_try_chat';
/**
 * The conversation row created for a draft, so a retry reuses it instead of
 * inserting a fresh conversation (and a fresh taste portrait) on every attempt.
 */
const TRY_CHAT_CONVERSATION_KEY = 'sekretly_try_chat_conversation';
/**
 * Set once Alma closes the conversation. The draft itself is saved on every
 * turn, so its mere presence doesn't mean it's finished — only a completed
 * draft should be resumed straight into the review/finalize step.
 */
const TRY_CHAT_COMPLETE_KEY = 'sekretly_try_chat_complete';

export function markTryChatComplete() {
  localStorage.setItem(TRY_CHAT_COMPLETE_KEY, '1');
}

/** The traveler kept talking after Alma wrapped up — the draft is open again. */
export function clearTryChatComplete() {
  localStorage.removeItem(TRY_CHAT_COMPLETE_KEY);
}

export function isTryChatComplete(): boolean {
  return localStorage.getItem(TRY_CHAT_COMPLETE_KEY) === '1';
}

export function readPendingTryChat(): Message[] | null {
  try {
    const raw = localStorage.getItem(TRY_CHAT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) && parsed.length > 0 ? (parsed as Message[]) : null;
  } catch {
    return null;
  }
}

export function clearPendingTryChat() {
  localStorage.removeItem(TRY_CHAT_STORAGE_KEY);
  localStorage.removeItem(TRY_CHAT_CONVERSATION_KEY);
  localStorage.removeItem(TRY_CHAT_COMPLETE_KEY);
  // The two-phase flow's answers belong to the same draft (absent on the classic journey).
  clearFlowDraft();
}

/**
 * Outcome of finishing a drafted chat. These are deliberately distinct so the
 * UI can say something true: "no hosts available" is a very different message
 * (and a different fix) from "something broke, retry", and neither should be
 * shown when the traveler simply isn't signed in yet.
 */
export type FinishResult =
  | 'matches'          // portrait built and hosts proposed — go to /matches
  | 'none'             // nothing pending; not an error
  | 'unauthenticated'  // signed up but no session yet (e.g. email unconfirmed)
  | 'no-hosts'         // finalize succeeded but proposed zero hosts
  | 'incomplete'       // server refused: the chat hasn't covered enough yet
  | 'error';           // finalize genuinely failed

/** What alma-finalize said was missing when it refused an incomplete chat. */
export type MissingTripPiece = 'conversation' | 'destination' | 'dates';

let lastIncompleteMissing: MissingTripPiece[] = [];

/** The `missing` list from the most recent 'incomplete' finish result. */
export function lastIncompleteReason(): MissingTripPiece[] {
  return lastIncompleteMissing;
}

/**
 * Alma's words for an 'incomplete' result: what she still needs, in plain
 * language, so the traveler knows what to tell her next.
 */
export function incompleteTripMessage(missing: MissingTripPiece[]): string {
  const where = missing.includes('destination');
  const when = missing.includes('dates');
  const need = where && when ? 'where and when you\'re travelling'
    : where ? 'which city you\'re headed to'
    : when ? 'the dates of your trip'
    : null;
  if (need) return `Before I map your taste, I still need to know ${need}. Tell me here and we'll pick it up from there.`;
  return "Before I map your taste, I'd love to hear a little more about you and your trip. Tell me here and we'll pick it up from there.";
}

/**
 * Ensures a `conversations` row exists for the pending draft, reusing one
 * from a previous attempt — otherwise every "try again" (or a re-opened
 * review modal) leaves behind another orphan conversation + taste portrait.
 * Returns null (and logs) if the traveler isn't authenticated or the insert
 * fails.
 */
export async function ensureTryChatConversation(messages: Message[]): Promise<string | null> {
  const existing = localStorage.getItem(TRY_CHAT_CONVERSATION_KEY);
  if (existing) {
    // The row keeps whatever transcript it was created with — and it's what
    // the trip chat later shows as the Alma history. If it was created on an
    // earlier pass (a retry, or a review that started before the chat was
    // finished), bring it up to date; never shrink it.
    const { data: stored } = await supabase
      .from('conversations')
      .select('messages')
      .eq('id', existing)
      .maybeSingle();
    const storedCount = Array.isArray(stored?.messages) ? stored.messages.length : 0;
    if (messages.length > storedCount) {
      const { error } = await supabase.from('conversations').update({ messages }).eq('id', existing);
      if (error) console.error('ensureTryChatConversation: could not update saved transcript', error);
    }
    return existing;
  }

  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return null;

  const { data: conv, error: convError } = await supabase
    .from('conversations')
    .insert({ traveler_id: session.user.id, messages, status: 'complete' })
    .select('id')
    .single();
  if (convError || !conv) {
    console.error('ensureTryChatConversation: could not save conversation', convError);
    return null;
  }
  const conversationId = conv.id as string;
  localStorage.setItem(TRY_CHAT_CONVERSATION_KEY, conversationId);
  return conversationId;
}

/**
 * Asks alma-finalize to synthesize a structured portrait from the transcript
 * without persisting anything or ranking hosts — used to pre-fill the trip
 * summary review modal. `answers` (two-phase chat only) are facts the server
 * puts over the model's reading of the transcript.
 */
export async function extractTryChatPortrait(
  conversationId: string,
  messages: Message[],
  answers?: AlmaFlowAnswers | null,
): Promise<StructuredPortrait | null> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return null;

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const res = await fetch(`${supabaseUrl}/functions/v1/alma-finalize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({
        mode: 'extract',
        conversationId,
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        ...(answers ? { answers } : {}),
      }),
    });
    if (!res.ok) {
      console.error('extractTryChatPortrait: extract returned', res.status);
      return null;
    }
    const data = await res.json().catch(() => null);
    return (data?.portrait as StructuredPortrait | undefined) ?? null;
  } catch (err) {
    console.error('extractTryChatPortrait: extract failed', err);
    return null;
  }
}

/**
 * Runs alma-finalize to generate (or accept a traveler-reviewed) taste
 * portrait and host proposals. The draft is left in place unless matches
 * were produced, so this stays retryable.
 */
export async function finishTryChatWithPortrait(
  conversationId: string,
  messages: Message[],
  portraitOverride?: StructuredPortrait,
  answers?: AlmaFlowAnswers | null,
): Promise<FinishResult> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.user) return 'unauthenticated';

  try {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL as string;
    const res = await fetch(`${supabaseUrl}/functions/v1/alma-finalize`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY as string,
      },
      body: JSON.stringify({
        conversationId,
        messages: messages.map((m) => ({ role: m.role, content: m.content })),
        ...(portraitOverride ? { portraitOverride } : {}),
        ...(answers ? { answers } : {}),
      }),
    });

    if (!res.ok) {
      // alma-finalize refuses (422) until the traveler has actually talked
      // with Alma and given a destination and dates — that's a "keep going",
      // not a failure to retry.
      if (res.status === 422) {
        const data = await res.json().catch(() => null);
        if (data?.error === 'incomplete_conversation') {
          lastIncompleteMissing = Array.isArray(data.missing) ? (data.missing as MissingTripPiece[]) : [];
          return 'incomplete';
        }
      }
      console.error('finishTryChatWithPortrait: finalize returned', res.status);
      return 'error';
    }

    const data = await res.json().catch(() => null);
    const hosts = (data?.proposedHosts as unknown[] | undefined) ?? [];
    if (hosts.length > 0) {
      clearPendingTryChat();
      return 'matches';
    }
    // A successful run that proposed nobody — the portrait is saved, there just
    // aren't any bookable hosts. Retrying changes nothing until that does.
    console.warn('finishTryChatWithPortrait: finalize proposed 0 hosts', data?.noHostsReason);
    return 'no-hosts';
  } catch (err) {
    console.error('finishTryChatWithPortrait: finalize failed', err);
    return 'error';
  }
}

/**
 * Completes a finished Alma chat once the traveler is authenticated: saves the
 * conversation and runs alma-finalize to generate the taste portrait + host
 * proposals directly (no review step). Kept for callers that don't need the
 * summary modal.
 */
export async function finishPendingTryChat(): Promise<FinishResult> {
  const messages = readPendingTryChat();
  if (!messages) return 'none';

  const conversationId = await ensureTryChatConversation(messages);
  if (!conversationId) return 'error';

  return finishTryChatWithPortrait(conversationId, messages, undefined, readDraftAnswers());
}
